/**
 * Disbursements — the money-out queue. Grouped by what an operator must do:
 * pay it, wait for it, or retry it.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, RefreshCw, Send } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/lendingCalculations";
import { staffApi } from "@/platform/staffApi";

type Row = Record<string, any>;

const intentTone: Record<string, string> = {
  succeeded: "bg-success/10 text-success",
  submitted: "bg-warning/10 text-warning",
  processing: "bg-warning/10 text-warning",
  failed: "bg-destructive/10 text-destructive",
  expired: "bg-destructive/10 text-destructive",
};

export function DisbursementsPage() {
  const { toast } = useToast();
  const [data, setData] = useState<Row | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await staffApi.disbursements());
    } catch (error) {
      toast({ title: "Could not load disbursements",
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const act = async (loanId: string, retry: boolean) => {
    setBusy(loanId);
    try {
      await (retry ? staffApi.retryDisbursement(loanId) : staffApi.disburse(loanId));
      toast({ title: retry ? "Retry submitted" : "Disbursement submitted",
        description: "Money is on its way — the queue updates when the provider confirms." });
      load();
    } catch (error) {
      toast({ title: retry ? "Retry failed" : "Disbursement failed",
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive" });
    } finally { setBusy(null); }
  };

  if (loading) return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin" />;
  if (!data) return null;

  const renderTable = (rows: Row[], kind: "awaiting" | "in_flight" | "failed" | "completed") => {
    if (rows.length === 0) {
      const empty = {
        awaiting: "Nothing waiting — every approved loan has been paid out.",
        in_flight: "Nothing in flight.",
        failed: "No failed payouts.",
        completed: "No completed disbursements yet.",
      }[kind];
      return <p className="py-10 text-center text-sm text-muted-foreground">{empty}</p>;
    }
    return (
      <Table>
        <TableHeader><TableRow>
          <TableHead>Borrower</TableHead>
          <TableHead className="text-right">Amount</TableHead>
          <TableHead>Loan</TableHead>
          <TableHead>{kind === "completed" ? "Disbursed" : "Approved"}</TableHead>
          {kind !== "awaiting" && <TableHead>Provider</TableHead>}
          {kind !== "completed" && <TableHead className="text-right">Action</TableHead>}
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.loan_id}>
              <TableCell>
                <p className="font-medium">{r.borrower_name || "—"}</p>
                <p className="text-xs text-muted-foreground">{r.borrower_phone}</p>
              </TableCell>
              <TableCell className="text-right font-medium tabular-nums">
                {formatCurrency(r.amount)}
              </TableCell>
              <TableCell className="font-mono text-xs">
                <Link to={`/staff/loans/${r.loan_id}`} className="text-primary hover:underline">
                  {r.loan_ref}
                </Link>
              </TableCell>
              <TableCell className="text-sm">
                {kind === "completed"
                  ? (r.disbursed_on ? new Date(r.disbursed_on).toLocaleDateString() : "—")
                  : (r.approved_on ? new Date(r.approved_on).toLocaleDateString() : "—")}
              </TableCell>
              {kind !== "awaiting" && (
                <TableCell>
                  {r.intent ? (
                    <>
                      <Badge className={intentTone[r.intent.status] || "bg-muted"}>
                        {String(r.intent.status).split("_").join(" ")}
                      </Badge>
                      {r.intent.reason && (
                        <p className="mt-1 max-w-[16rem] text-xs text-destructive">
                          {r.intent.reason}
                        </p>
                      )}
                    </>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
              )}
              {kind !== "completed" && (
                <TableCell className="text-right">
                  {kind === "in_flight" ? (
                    <span className="text-xs text-muted-foreground">awaiting provider</span>
                  ) : (
                    <Button size="sm" disabled={busy === r.loan_id}
                      variant={kind === "failed" ? "outline" : "default"}
                      onClick={() => act(r.loan_id, kind === "failed")}>
                      {busy === r.loan_id
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <><Send className="mr-1 h-4 w-4" />{kind === "failed" ? "Retry" : "Disburse"}</>}
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Disbursements</h1>
          <p className="text-sm text-muted-foreground">
            Money out — what is waiting, in flight, or needs a retry.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="mr-1 h-4 w-4" />Refresh
        </Button>
      </header>

      <div className="grid gap-4 sm:grid-cols-3">
        {([
          ["Awaiting payout", data.totals.awaiting, data.awaiting.length, false],
          ["In flight", data.totals.in_flight, data.in_flight.length, false],
          ["Failed — needs attention", data.totals.failed, data.failed.length, true],
        ] as [string, number, number, boolean][]).map(([label, amount, count, alert]) => (
          <Card key={label} className={alert && count > 0 ? "border-destructive/40" : undefined}>
            <CardContent className="pt-5">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className={`mt-1 text-2xl font-bold tabular-nums ${
                alert && count > 0 ? "text-destructive" : ""}`}>
                {formatCurrency(amount)}
              </p>
              <p className="text-xs text-muted-foreground">
                {count} loan{count === 1 ? "" : "s"}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {data.failed.length > 0 && (
        <Card className="border-destructive/40">
          <CardContent className="flex items-center gap-3 py-4 text-sm">
            <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
            <p>
              {data.failed.length} approved loan{data.failed.length === 1 ? "" : "s"} did not reach
              the borrower. They are waiting on money you have already promised — retry below.
            </p>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="awaiting">
        <TabsList>
          <TabsTrigger value="awaiting">Awaiting ({data.awaiting.length})</TabsTrigger>
          <TabsTrigger value="in_flight">In flight ({data.in_flight.length})</TabsTrigger>
          <TabsTrigger value="failed">Failed ({data.failed.length})</TabsTrigger>
          <TabsTrigger value="completed">Completed ({data.completed.length})</TabsTrigger>
        </TabsList>
        {(["awaiting", "in_flight", "failed", "completed"] as const).map((k) => (
          <TabsContent key={k} value={k} className="pt-4">
            <Card><CardContent className="pt-4">{renderTable(data[k], k)}</CardContent></Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
